import type { INPMetricWithAttribution } from 'web-vitals/attribution';

/**
 * Reports INP to Plausible, with the attribution that says what to fix.
 *
 * Search Console reports one number per url group, which is not enough to act on: INP is the sum
 * of the wait before the handler runs, the handler itself and the paint that follows, and each of
 * those has a different cure. The attribution build names the element that was interacted with and
 * splits the time between those three, so the event below says which interaction on which kind of
 * page is slow, and in which phase the time goes.
 *
 * Every property is bucketed or picked from a small set. Plausible counts unique property values,
 * and a raw millisecond figure or a full url would make a dimension nobody can read.
 *
 * Only interactions Google would hold against us are reported - `good` is under the same 200ms
 * that Search Console draws the line at. Plausible bills custom events beside pageviews, and the
 * pages that already respond quickly have nothing to tell us.
 */

export type PlausibleProps = Record<string, string>;

/** the page kinds as the urls spell them, which is the same in every market */
export function pageKind(pathname: string): string {
	if (pathname === '/') return 'home';
	if (pathname.includes('-p-')) return 'product';
	if (pathname.includes('-c-') || pathname.startsWith('/c/')) return 'category';
	return 'other';
}

/** wide enough buckets to stay readable, narrow around the 200ms threshold that matters */
export function durationBucket(ms: number): string {
	if (ms < 100) return '<100';
	if (ms < 200) return '100-200';
	if (ms < 300) return '200-300';
	if (ms < 500) return '300-500';
	if (ms < 1000) return '500-1000';
	return '1000+';
}

/** the phase holding most of the interaction, which is the one worth attacking first */
export function slowestPhase(attribution: INPMetricWithAttribution['attribution']): string {
	const phases: Array<[string, number]> = [
		['input delay', attribution.inputDelay],
		['processing', attribution.processingDuration],
		['presentation', attribution.presentationDelay],
	];
	return phases.reduce((slowest, phase) => (phase[1] > slowest[1] ? phase : slowest))[0];
}

/**
 * Dom ids that carry a number are records, not controls - `Select` writes the delivery address's
 * own id onto its options, so a slow click in the checkout would otherwise send a customer's
 * address id to Plausible, and give the dimension a value nobody will ever see twice.
 */
export function maskRecordIds(target: string): string {
	return target.replace(/#[\w-]*\d[\w-]*/g, '#id');
}

/**
 * A selector has to fit in a report, and what it is cut down to has to stay distinguishable: the
 * element interacted with sits at the right-hand end, `content-left > … > button.buy`, so the
 * ancestors are what goes.
 */
export function shortenTarget(target: string | undefined): string {
	if (!target) return 'unknown';
	const masked = maskRecordIds(target);
	return masked.length > 80 ? `…${masked.slice(-79)}` : masked;
}

/** where the visitor was when an interaction began, kept per interaction rather than read later */
export type VisitedRoute = { time: number; pathname: string };

/**
 * The route an interaction started on. A click on a link changes the location inside the click
 * handler, long before the interaction is measured, so the location at reporting time is the page
 * the visitor landed on rather than the one whose button was slow.
 */
export function routeAt(routes: VisitedRoute[], time: number | undefined, fallback: string): string {
	if (time === undefined) return routes[routes.length - 1]?.pathname ?? fallback;
	const started = routes.filter((route) => route.time <= time);
	return (started.length ? started[started.length - 1] : routes[0])?.pathname ?? fallback;
}

export function inpProps(metric: INPMetricWithAttribution, pathname: string): PlausibleProps {
	const { attribution } = metric;

	return {
		page: pageKind(pathname),
		rating: metric.rating,
		inp: durationBucket(metric.value),
		phase: slowestPhase(attribution),
		interaction: attribution.interactionType ?? 'unknown',
		target: shortenTarget(attribution.interactionTarget),
		// an interaction during load is a different problem from one on a settled page
		load: attribution.loadState,
	};
}

/**
 * What makes a report the same one twice. web-vitals forces a final report when the page is
 * hidden, whether or not anything changed since the last one, so without this an ordinary visit
 * with one slow interaction would report - and Plausible would bill - that interaction twice.
 */
export function reportKey(metric: INPMetricWithAttribution): string {
	return `${metric.id}:${metric.value}`;
}

function sendToPlausible(props: PlausibleProps) {
	window.plausible('INP', { props });
}

/**
 * Starts reporting once, from the client entry. The library is loaded on its own rather than with
 * the app: it has nothing to do until an interaction happens, and measuring must not add to what
 * it measures. Without Plausible on the page there is nowhere to report, so nothing loads at all.
 *
 * Reported as the page's INP is established and each time a slower interaction replaces it, rather
 * than once the page is hidden, which is the default. The storefront is a single page app: by the
 * time a hidden page reports, the visitor may be two routes further on, and the event would name
 * the wrong kind of page. What this does not see is a slow interaction that never becomes the
 * page's worst - which is also what Search Console does not see, since INP is that worst one.
 */
export function reportInp(send: (props: PlausibleProps) => void = sendToPlausible): void {
	if (typeof window === 'undefined' || typeof window.plausible !== 'function') return;

	/*
	 * One entry per route the visitor interacted on, not per interaction: the reportedcandidate
	 * can be an older interaction once a visit is long enough, and its route has to still be here.
	 * Interactions on a route already recorded add nothing, since the entry before them answers
	 * for them, so a long visit costs one entry per page it passes through.
	 *
	 * The event's own timestamp rather than the time the listener ran: web-vitals reports the
	 * interaction's start time, and a route recorded a fraction later would look like the next one.
	 */
	const routes: VisitedRoute[] = [{ time: 0, pathname: window.location.pathname }];
	const noteRoute = (event: Event) => {
		const { pathname } = window.location;
		if (routes[routes.length - 1]?.pathname === pathname) return;
		routes.push({ time: event.timeStamp, pathname });
	};
	for (const event of ['pointerdown', 'keydown'] as const) {
		window.addEventListener(event, noteRoute, { capture: true, passive: true });
	}

	import('web-vitals/attribution')
		.then(({ onINP }) => {
			let lastReported = '';
			onINP(
				(metric) => {
					if (metric.rating === 'good' || reportKey(metric) === lastReported) return;
					lastReported = reportKey(metric);
					const pathname = routeAt(routes, metric.attribution.interactionTime, window.location.pathname);
					send(inpProps(metric, pathname));
				},
				{ reportAllChanges: true }
			);
		})
		.catch(() => {
			// measurement is not worth an error dialog, or a report of its own
		});
}
